/*
 * preen-cart-count.js - unit cart-count-badge (Jonah 2026-10-08: 'add a little exponent to the shopping cart icon that
 * displays the amount of items in the cart, stylized to the store as well').
 * The header cart badges (span.preen-cart-count in snippets/preen-header.liquid: one in the phone/tablet CART link, one
 * in the laptop/desktop CART link) are rendered from cart.item_count, so the first paint is right. This script keeps
 * them right without a reload: it reads GET /cart.js and writes every badge's number ('99+' above 99), its hidden state
 * (hidden at 0) and its screen-reader text (', N item' / ', N items', part of the link's accessible name)
 *   - after a successful same-origin request to /cart/add, /cart/change, /cart/update or /cart/clear (with or without
 *     .js, any locale prefix), made with fetch or XMLHttpRequest (the product form, the Treat Finder, the cart page);
 *   - after the store's own 'preenpf:cart-added' event (vendored runtime and Treat Finder, after a successful add);
 *   - on a 'preen:cart:changed' event (on document or window; any script may fire it after it changes the cart);
 *   - when the page is shown again from the back/forward cache (pageshow with persisted) or reached by a back/forward
 *     navigation, where the server-rendered number can be old. A fresh page load reads nothing: its number is current.
 * The wrappers never change, delay or re-order the original request: fetch returns the very promise the browser gave
 * and only watches it; XMLHttpRequest.open runs first and then only notes whether the URL is a cart change and listens
 * for its load. Bursts of triggers collapse into one read (30 ms), and a trigger during a read is followed by one more
 * read whose answer wins, so the badge always ends on the newest cart.
 * Vanilla JS, no dependencies, no storage, no cookies; its only request is GET /cart.js on this store.
 * window.__preenCartCount guards a second load (the header snippet is rendered twice on some pages).
 */
(function () {
  'use strict';
  if (window.__preenCartCount) return;
  window.__preenCartCount = true;

  var BADGE = '.preen-cart-count';
  var NUM = '.preen-cart-count__n';
  var SR = '.preen-cart-count__sr';
  var CHANGE = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/;
  var WAIT = 30;                      // ms: triggers within this window collapse into one read

  var nativeFetch = typeof window.fetch === 'function' ? window.fetch : null;
  var timer = 0, reading = false, again = false;
  var firstShow = document.readyState !== 'complete';

  function cartRoot() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }

  // a request URL that changes this store's cart
  function isChange(url) {
    try {
      var u = new URL(String(url), window.location.href);
      return u.origin === window.location.origin && CHANGE.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  // every badge on the page: number, hidden state and screen-reader text; writes only what differs
  function render(count) {
    var n = Math.max(0, Math.floor(Number(count) || 0));
    var label = n > 99 ? '99+' : String(n);
    var spoken = ', ' + n + (n === 1 ? ' item' : ' items');
    var list = document.querySelectorAll(BADGE);
    for (var i = 0; i < list.length; i++) {
      var b = list[i], num = b.querySelector(NUM), sr = b.querySelector(SR);
      if (num && num.textContent !== label) num.textContent = label;
      if (sr && sr.textContent !== spoken) sr.textContent = spoken;
      // the label's length (1, 2 or 3 characters) sizes the phone bubble and how far CART moves over for it;
      // written before the bubble is shown so the move and the bubble land in the same frame
      if (b.getAttribute('data-preen-len') !== String(label.length)) b.setAttribute('data-preen-len', String(label.length));
      if (n > 0) {
        if (b.hasAttribute('hidden')) b.removeAttribute('hidden');
      } else if (!b.hasAttribute('hidden')) {
        b.setAttribute('hidden', '');
      }
    }
  }

  // GET /cart.js; a trigger that arrives while it runs means one more read, and only that last answer is written
  function read() {
    timer = 0;
    if (reading) { again = true; return; }
    reading = true;
    again = false;
    var p;
    try {
      p = nativeFetch.call(window, cartRoot() + 'cart.js', {
        method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
    } catch (e) {
      reading = false;
      return;
    }
    p.then(function (r) {
      return r.ok ? r.json() : null;
    }).then(function (cart) {
      if (!again && cart && typeof cart.item_count === 'number') render(cart.item_count);
    }).catch(function () {
      /* the badge keeps its number */
    }).then(function () {
      reading = false;
      if (again) read();
    });
  }

  function refresh() {
    if (!nativeFetch) return;
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(read, WAIT);
  }

  // fetch: the caller gets the original promise; a successful cart change also schedules a read
  if (nativeFetch) {
    try {
      window.fetch = function (input) {
        var p = nativeFetch.apply(window, arguments);
        try {
          var url = input && typeof input === 'object' && 'url' in input ? input.url : input;
          if (isChange(url) && p && typeof p.then === 'function') {
            p.then(function (r) { if (r && r.ok) refresh(); }, function () { /* the caller handles it */ });
          }
        } catch (e) { /* never in the way */ }
        return p;
      };
    } catch (e) { /* fetch not writable: events and pageshow still work */ }
  }

  // XMLHttpRequest: open runs first, untouched; a cart-change request gets one load listener
  var xp = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (xp && typeof xp.open === 'function' && typeof window.WeakMap === 'function') {
    var nativeOpen = xp.open;
    var cartXhr = new WeakMap();
    var onXhrLoad = function () {
      if (cartXhr.get(this) && this.status >= 200 && this.status < 300) refresh();
    };
    try {
      xp.open = function (method, url) {
        var r = nativeOpen.apply(this, arguments);
        try {
          var hit = isChange(url);
          if (hit && !cartXhr.has(this)) this.addEventListener('load', onXhrLoad);
          if (hit || cartXhr.has(this)) cartXhr.set(this, hit);
        } catch (e) { /* never in the way */ }
        return r;
      };
    } catch (e) { /* open not writable */ }
  }

  document.addEventListener('preenpf:cart-added', refresh);
  document.addEventListener('preen:cart:changed', refresh);
  window.addEventListener('preen:cart:changed', refresh);

  // back/forward: a page restored from the bfcache, or a back/forward load that may come from the HTTP cache
  function backForward() {
    try {
      var nav = window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      return !!nav && nav.type === 'back_forward';
    } catch (e) {
      return false;
    }
  }
  window.addEventListener('pageshow', function (e) {
    var initial = firstShow;
    firstShow = false;
    if (initial && !e.persisted && !backForward()) return;
    refresh();
  });
})();
